<template>
  <div
    role="radiogroup"
    class="radio-card-group"
    :aria-label="ariaLabel"
    :aria-disabled="disabled || undefined"
    :style="{
      '--radio-card-group-columns': columns ?? options.length,
    }"
    data-qa="radio-card-group"
    @keydown="onKeydown"
  >
    <template
      v-for="(option, index) in options"
      :key="option.value"
    >
      <div
        v-if="option.startsRow && index > 0"
        class="radio-card-group__row-break"
        aria-hidden="true"
        data-qa="radio-card-group__row-break"
      />
      <div
        ref="optionElements"
        role="radio"
        class="radio-card-group__option"
        :class="{
          'radio-card-group__option_selected': option.value === modelValue,
          'radio-card-group__option_disabled': isOptionDisabled(option),
          'radio-card-group__option_wide': option.isWide,
        }"
        :aria-checked="option.value === modelValue"
        :aria-disabled="isOptionDisabled(option) || undefined"
        :tabindex="index === focusableIndex ? 0 : -1"
        data-qa="radio-card-group__option"
        :data-qa-id="option.value"
        @click="select(option)"
      >
        <span
          class="radio-card-group__indicator"
          aria-hidden="true"
        />
        <div class="radio-card-group__content">
          <slot
            name="option"
            :option="option"
            :is-selected="option.value === modelValue"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts" generic="VALUE extends string">
import {
  computed,
  useTemplateRef,
} from 'vue';

import {
  IRadioCardOption,
  IRadioCardOptionSlotProps,
} from './radio-card-group.ts';

const props = defineProps<{
  modelValue: VALUE | undefined,
  options: IRadioCardOption<VALUE>[],
  ariaLabel: string,
  /** Number of cards per row. Defaults to the number of options. */
  columns?: number,
  disabled?: boolean,
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: VALUE): void,
}>();

defineSlots<{
  option(props: IRadioCardOptionSlotProps<VALUE>): any,
}>();

const optionElements = useTemplateRef<HTMLElement[]>('optionElements');

function isOptionDisabled(option: IRadioCardOption<VALUE>): boolean {
  return props.disabled || !!option.disabled;
}

const enabledIndices = computed<number[]>(() => props.options
  .map((option, index) => (isOptionDisabled(option) ? -1 : index))
  .filter((index) => index >= 0));

/** Only one card is in the tab order: the selected one, otherwise the first enabled one. */
const focusableIndex = computed<number | undefined>(() => {
  const selectedIndex = props.options.findIndex((option) => option.value === props.modelValue);
  if (enabledIndices.value.includes(selectedIndex)) {
    return selectedIndex;
  }
  return enabledIndices.value[0];
});

function select(option: IRadioCardOption<VALUE>) {
  if (isOptionDisabled(option) || option.value === props.modelValue) {
    return;
  }
  emit('update:modelValue', option.value);
}

function selectAndFocus(index: number) {
  select(props.options[index]);
  optionElements.value?.[index]?.focus();
}

function getFocusedIndex(): number {
  const focusedElement = document.activeElement;
  return optionElements.value?.findIndex((element) => element === focusedElement) ?? -1;
}

function onKeydown(event: KeyboardEvent) {
  const enabled = enabledIndices.value;
  if (enabled.length === 0) {
    return;
  }
  const focusedIndex = getFocusedIndex();
  const position = enabled.indexOf(focusedIndex);
  let targetIndex: number | undefined;
  switch (event.key) {
    case 'ArrowRight':
    case 'ArrowDown':
      targetIndex = enabled[(position + 1) % enabled.length];
      break;
    case 'ArrowLeft':
    case 'ArrowUp':
      targetIndex = enabled[position <= 0 ? enabled.length - 1 : position - 1];
      break;
    case 'Home':
      targetIndex = enabled[0];
      break;
    case 'End':
      targetIndex = enabled[enabled.length - 1];
      break;
    case ' ':
    case 'Enter':
      targetIndex = position >= 0 ? focusedIndex : undefined;
      break;
    default:
      return;
  }
  event.preventDefault();
  if (targetIndex !== undefined) {
    selectAndFocus(targetIndex);
  }
}
</script>

<style scoped lang="scss">
.radio-card-group {
  display: grid;
  grid-template-columns: repeat(var(--radio-card-group-columns), minmax(0, 1fr));
  gap: var(--gap);

  &__option {
    display: flex;
    align-items: flex-start;
    gap: var(--gap-union);
    padding: var(--gap);
    border: 1px solid var(--border-color-strong);
    border-radius: var(--border-radius);
    background-color: var(--input-bg);
    cursor: pointer;

    &:hover {
      border-color: var(--secondary-bg-hover-color);
    }

    &:focus-visible {
      outline: none;
      box-shadow: var(--focus-shadow);
    }
  }

  &__option_selected,
  &__option_selected:hover {
    border-color: var(--secondary-bg-color);
    background-color: var(--color-slate-1);
    box-shadow: inset 0 0 0 1px var(--secondary-bg-color);
  }

  &__option_disabled {
    cursor: default;
    opacity: 0.6;

    &:hover {
      border-color: var(--border-color-strong);
    }
  }

  &__option_wide {
    grid-column: 1 / -1;
  }

  // Takes a whole row of the grid, or of a group that wraps its cards as a flex row.
  &__row-break {
    grid-column: 1 / -1;
    flex-basis: 100%;
  }

  &__indicator {
    flex-shrink: 0;
    width: 1rem;
    height: 1rem;
    margin-top: 0.125rem;
    border: 1px solid var(--border-color-strong);
    border-radius: 50%;
    background-color: var(--input-bg);
  }

  &__option_selected &__indicator {
    border: 0.3125rem solid var(--secondary-bg-color);
  }

  &__content {
    flex: 1 1 auto;
    min-width: 0;
  }
}
</style>
